import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plug, Key, Plus, Trash2, Copy, Check, Eye, EyeOff,
  Terminal, Cpu, BookOpen, Shield, Zap, RefreshCw,
  ChevronDown, ChevronRight, Globe, Code, AlertCircle, CheckCircle2,
  ExternalLink, Bot, Wrench, Clock, BarChart3, MessageCircle, Phone, Unlink
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const MCP_ENDPOINT = `${process.env.REACT_APP_BACKEND_URL || ''}/api/mcp`;

const MCP_CLIENTS = [
  {
    id: 'claude-desktop',
    name: 'Claude Desktop',
    icon: '🤖',
    description: 'App oficial de Anthropic para macOS y Windows',
    config_key: 'claude_desktop',
    download: 'https://claude.ai/download',
    color: 'from-orange-500/20 to-amber-500/20',
    border: 'border-orange-500/30',
    badge_color: 'bg-orange-500/20 text-orange-300',
  },
  {
    id: 'cursor',
    name: 'Cursor IDE',
    icon: '⚡',
    description: 'Editor de código con IA integrada',
    config_key: 'cursor',
    download: 'https://cursor.sh',
    color: 'from-blue-500/20 to-cyan-500/20',
    border: 'border-blue-500/30',
    badge_color: 'bg-blue-500/20 text-blue-300',
  },
  {
    id: 'windsurf',
    name: 'Windsurf',
    icon: '🌊',
    description: 'IDE con agentes de IA de Codeium',
    config_key: 'cursor',
    download: 'https://codeium.com/windsurf',
    color: 'from-teal-500/20 to-emerald-500/20',
    border: 'border-teal-500/30',
    badge_color: 'bg-teal-500/20 text-teal-300',
  },
  {
    id: 'custom',
    name: 'Cliente personalizado',
    icon: '🔧',
    description: 'Cualquier cliente compatible con MCP + JSON-RPC 2.0',
    config_key: 'custom',
    download: null,
    color: 'from-purple-500/20 to-violet-500/20',
    border: 'border-purple-500/30',
    badge_color: 'bg-purple-500/20 text-purple-300',
  },
];

const TOOL_CATEGORIES = {
  notes: { label: 'Notas', emoji: '📝', color: 'text-yellow-400' },
  reminders: { label: 'Recordatorios', emoji: '⏰', color: 'text-orange-400' },
  calendar: { label: 'Calendario', emoji: '📅', color: 'text-blue-400' },
  finance: { label: 'Finanzas', emoji: '💰', color: 'text-green-400' },
  health: { label: 'Salud', emoji: '💪', color: 'text-red-400' },
  lists: { label: 'Listas', emoji: '✅', color: 'text-cyan-400' },
  knowledge: { label: 'Búsqueda', emoji: '🔍', color: 'text-violet-400' },
  rapidapi: { label: 'APIs Externas', emoji: '⚡', color: 'text-amber-400' },
  feeds: { label: 'Feeds', emoji: '📡', color: 'text-pink-400' },
  agents: { label: 'Agentes IA', emoji: '🤖', color: 'text-indigo-400' },
};

function getConfig(client_id, token, user_id) {
  const endpoint = `${window.location.origin}/api/mcp`;
  const sse = `${window.location.origin}/api/mcp/sse`;

  if (client_id === 'claude_desktop') {
    return JSON.stringify({
      mcpServers: {
        okbot: {
          command: "npx",
          args: ["-y", "@modelcontextprotocol/server-http-proxy", endpoint],
          env: {
            MCP_SERVER_URL: endpoint,
            AUTHORIZATION: `Bearer ${token}`,
          },
        },
      },
    }, null, 2);
  }

  if (client_id === 'cursor') {
    return JSON.stringify({
      mcpServers: {
        okbot: {
          url: endpoint,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      },
    }, null, 2);
  }

  return JSON.stringify({
    server: endpoint,
    sse: sse,
    auth: { type: "Bearer", token: token },
    protocol: "MCP/2024-11-05",
    user_id: user_id,
  }, null, 2);
}

// ── Sub-components ────────────────────────────────────────────────────────────

const CopyButton = ({ text, label = '' }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button onClick={copy}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all text-xs font-medium">
      {copied ? <><Check size={12} className="text-green-400" /><span className="text-green-400">Copiado</span></> : <><Copy size={12} />{label || 'Copiar'}</>}
    </button>
  );
};

const TokenCard = ({ token, onRevoke }) => {
  const [expanded, setExpanded] = useState(false);
  const [selectedClient, setSelectedClient] = useState('claude_desktop');

  const daysAgo = token.last_used_at
    ? Math.round((Date.now() - new Date(token.last_used_at).getTime()) / 86400000)
    : null;

  return (
    <div className="glass-panel rounded-2xl overflow-hidden">
      <div className="p-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${token.active ? 'bg-green-500/15' : 'bg-red-500/10'}`}>
            <Key size={18} className={token.active ? 'text-green-400' : 'text-red-400'} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-white font-medium text-sm truncate">{token.name}</p>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${token.active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                {token.active ? 'Activo' : 'Revocado'}
              </span>
            </div>
            <div className="flex items-center gap-3 mt-0.5">
              <span className="text-gray-500 text-xs font-mono">{token.token_prefix}••••••••</span>
              {token.use_count > 0 && (
                <span className="text-gray-600 text-xs flex items-center gap-1">
                  <BarChart3 size={10} />{token.use_count} usos
                </span>
              )}
              {daysAgo !== null && (
                <span className="text-gray-600 text-xs flex items-center gap-1">
                  <Clock size={10} />Usado hace {daysAgo}d
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {token.active && (
            <button onClick={() => setExpanded(v => !v)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all text-xs">
              <Code size={12} />{expanded ? 'Ocultar' : 'Ver config'}
              {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            </button>
          )}
          {token.active && (
            <button onClick={() => onRevoke(token.token_id)}
              className="p-2 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-all">
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-white/5 p-4 space-y-3 bg-black/20">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-gray-500 text-xs">Configuración para:</span>
            {['claude_desktop', 'cursor', 'custom'].map(c => (
              <button key={c} onClick={() => setSelectedClient(c)}
                className={`text-xs px-2.5 py-1 rounded-lg transition-all ${selectedClient === c ? 'bg-white/15 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}>
                {c === 'claude_desktop' ? '🤖 Claude Desktop' : c === 'cursor' ? '⚡ Cursor/Windsurf' : '🔧 Personalizado'}
              </button>
            ))}
          </div>
          <div className="relative">
            <pre className="text-xs text-green-300 bg-black/40 p-4 rounded-xl overflow-x-auto font-mono leading-relaxed border border-white/5">
              {getConfig(selectedClient, `${token.token_prefix}••••••••`, token.user_id)}
            </pre>
            <div className="absolute top-2 right-2">
              <span className="text-[10px] text-gray-600 bg-black/60 px-2 py-0.5 rounded">El token real se muestra al crear</span>
            </div>
          </div>
          <p className="text-gray-600 text-xs flex items-center gap-1.5">
            <AlertCircle size={11} />Genera el token una vez y guárdalo de forma segura — no se puede recuperar después.
          </p>
        </div>
      )}
    </div>
  );
};

// ── Main Module ───────────────────────────────────────────────────────────────

const IntegrationsModule = ({ user }) => {
  const [tokens, setTokens] = useState([]);
  const [stats, setStats] = useState({});
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [newTokenName, setNewTokenName] = useState('');
  const [activeTab, setActiveTab] = useState('whatsapp');
  const [waStatus, setWaStatus] = useState(null);
  const [waLoading, setWaLoading] = useState(false);
  const [waUnlinking, setWaUnlinking] = useState(false);
  const [newToken, setNewToken] = useState(null);
  const [showToken, setShowToken] = useState(false);
  const [expandedClient, setExpandedClient] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [serverStatus, setServerStatus] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [tokensRes, statsRes, toolsRes, statusRes, waRes] = await Promise.allSettled([
        axios.get(`${API}/tokens/list/${user.id}`),
        axios.get(`${API}/tokens/stats/${user.id}`),
        axios.get(`${API}/mcp/tools`),
        axios.get(`${API}/mcp/stats`),
        axios.get(`${API}/whatsapp/user/status/${user.id}`),
      ]);
      if (tokensRes.status === 'fulfilled') setTokens(tokensRes.value.data.tokens || []);
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
      if (toolsRes.status === 'fulfilled') setTools(toolsRes.value.data.tools || []);
      if (statusRes.status === 'fulfilled') setServerStatus(statusRes.value.data);
      if (waRes.status === 'fulfilled') setWaStatus(waRes.value.data);
    } catch (e) {}
    setLoading(false);
  }, [user.id]);

  const handleWaUnlink = async () => {
    if (!window.confirm('¿Deseas desvincular tu WhatsApp de OkBot?')) return;
    setWaUnlinking(true);
    try {
      await axios.delete(`${API}/whatsapp/user/unlink/${user.id}`);
      setWaStatus({ linked: false, phone: null });
      toast.success('WhatsApp desvinculado correctamente');
    } catch {
      toast.error('Error al desvincular');
    } finally { setWaUnlinking(false); }
  };

  useEffect(() => { loadData(); }, [loadData]);

  const generateToken = async () => {
    if (!newTokenName.trim()) { toast.error('Escribe un nombre para identificar este token'); return; }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/tokens/generate`, {
        user_id: user.id,
        name: newTokenName.trim(),
        scopes: ['read', 'write', 'tools'],
      });
      setNewToken(res.data);
      setNewTokenName('');
      setShowToken(false);
      toast.success('Token generado — guárdalo ahora, no podrás verlo de nuevo');
      await loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al generar el token');
    }
    setGenerating(false);
  };

  const revokeToken = async (tokenId) => {
    if (!window.confirm('¿Revocar este token? Las integraciones que lo usen dejarán de funcionar.')) return;
    try {
      await axios.delete(`${API}/tokens/${tokenId}`, { data: { user_id: user.id } });
      toast.success('Token revocado');
      await loadData();
    } catch (e) {
      toast.error('Error al revocar el token');
    }
  };

  const toolsByCategory = tools.reduce((acc, t) => {
    const cat = t.category || 'general';
    acc[cat] = acc[cat] || [];
    acc[cat].push(t);
    return acc;
  }, {});

  const TABS = [
    { id: 'whatsapp', label: 'Chat WhatsApp', icon: MessageCircle },
    { id: 'tokens', label: 'Mis tokens', icon: Key },
    { id: 'connect', label: 'Conectar', icon: Plug },
    { id: 'tools', label: 'Herramientas', icon: Wrench },
    { id: 'guide', label: 'Guía rápida', icon: BookOpen },
  ];

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-5">

      {/* Header */}
      <div className="glass-panel p-6 rounded-2xl bg-gradient-to-r from-violet-500/10 to-blue-500/10 border border-violet-500/20">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-violet-500/30">
            <Plug size={26} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-white">Integraciones MCP</h1>
            <p className="text-gray-400 text-sm mt-1">
              Conecta Claude Desktop, Cursor u otros asistentes de IA directamente a tus datos de OkBot
              — notas, calendario, finanzas, salud y mucho más — en tiempo real.
            </p>
            {serverStatus && (
              <div className="flex items-center gap-4 mt-3 flex-wrap">
                <span className="flex items-center gap-1.5 text-green-400 text-xs font-medium">
                  <CheckCircle2 size={13} />Servidor MCP operativo
                </span>
                <span className="text-gray-600 text-xs">{serverStatus.tools} herramientas · {serverStatus.total_executions || 0} ejecuciones</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Tokens activos', value: stats.active_tokens ?? '—', icon: Key, color: 'text-violet-400' },
          { label: 'Total de usos', value: stats.total_uses ?? '—', icon: Zap, color: 'text-amber-400' },
          { label: 'Herramientas', value: tools.length || '—', icon: Cpu, color: 'text-blue-400' },
        ].map(s => (
          <div key={s.label} className="glass-panel p-4 rounded-2xl text-center">
            <s.icon size={20} className={`${s.color} mx-auto mb-1.5`} />
            <p className="text-white font-bold text-xl">{s.value}</p>
            <p className="text-gray-500 text-xs mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white/5 p-1 rounded-2xl overflow-x-auto">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap flex-1 justify-center ${activeTab === t.id ? 'bg-white/15 text-white shadow' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
              <Icon size={15} />{t.label}
            </button>
          );
        })}
      </div>

      {/* ── TAB: Chat por WhatsApp ── */}
      {activeTab === 'whatsapp' && (
        <div className="space-y-5">
          {/* Header card */}
          <div className="glass-panel p-6 rounded-2xl bg-gradient-to-br from-green-600/15 to-emerald-700/10 border border-green-500/25">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-green-500/20 border border-green-500/30 flex items-center justify-center flex-shrink-0">
                <MessageCircle size={26} className="text-green-400" />
              </div>
              <div>
                <h2 className="text-white font-bold text-lg">Chat por WhatsApp</h2>
                <p className="text-gray-400 text-sm mt-0.5">
                  Conecta tu número personal de WhatsApp para chatear con OkBot directamente desde la app
                </p>
              </div>
            </div>
          </div>

          {/* Status */}
          {loading ? (
            <div className="flex justify-center py-8"><RefreshCw size={20} className="text-green-400 animate-spin" /></div>
          ) : waStatus?.linked ? (
            /* Linked state */
            <div className="space-y-4">
              <div className="glass-panel p-5 rounded-2xl border border-green-500/25 bg-green-500/5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-green-500/20 border-2 border-green-500/40 flex items-center justify-center">
                    <Check size={22} className="text-green-400" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">WhatsApp vinculado</p>
                    <p className="text-green-300 text-sm font-mono">{waStatus.phone}</p>
                    {waStatus.linked_at && (
                      <p className="text-white text-xs mt-0.5">
                        Vinculado el {new Date(waStatus.linked_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4">
                  {[
                    { e: '📝', t: 'Notas y recordatorios' },
                    { e: '📅', t: 'Agenda y eventos' },
                    { e: '💰', t: 'Finanzas personales' },
                    { e: '🤖', t: 'Agentes especializados' },
                  ].map(f => (
                    <div key={f.e} className="bg-white/5 rounded-xl p-3 flex items-center gap-2">
                      <span className="text-lg">{f.e}</span>
                      <span className="text-white text-xs font-medium">{f.t}</span>
                    </div>
                  ))}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={handleWaUnlink}
                    disabled={waUnlinking}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-400 text-sm font-medium transition-colors disabled:opacity-50"
                  >
                    {waUnlinking ? <RefreshCw size={14} className="animate-spin" /> : <Unlink size={14} />}
                    {waUnlinking ? 'Desvinculando...' : 'Desvincular WhatsApp'}
                  </button>
                  <button
                    onClick={loadData}
                    className="p-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                  >
                    <RefreshCw size={14} />
                  </button>
                </div>
              </div>

              {/* Usage tips */}
              <div className="glass-panel p-5 rounded-2xl space-y-3">
                <h3 className="text-white font-semibold text-sm">¿Cómo usar OkBot desde WhatsApp?</h3>
                {[
                  { cmd: '"Recuérdame comprar leche mañana a las 10am"', desc: 'Crea recordatorios en lenguaje natural' },
                  { cmd: '"¿Cuánto gasté este mes?"', desc: 'Consulta tu resumen financiero' },
                  { cmd: '"Agrega reunión con el equipo el viernes a las 3pm"', desc: 'Crea eventos en tu calendario' },
                  { cmd: '"¿Cuáles son mis tareas pendientes?"', desc: 'Ve tus listas y notas' },
                ].map(tip => (
                  <div key={tip.cmd} className="bg-white/5 rounded-xl p-3">
                    <p className="text-green-300 text-xs font-medium italic">"{tip.cmd}"</p>
                    <p className="text-white text-xs mt-1">→ {tip.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* Not linked state */
            <div className="glass-panel p-6 rounded-2xl text-center space-y-5">
              <div className="w-16 h-16 rounded-full bg-green-500/10 border-2 border-green-500/20 flex items-center justify-center mx-auto">
                <Phone size={28} className="text-green-400" />
              </div>
              <div>
                <p className="text-white font-semibold text-lg">Aún no tienes WhatsApp vinculado</p>
                <p className="text-white text-sm mt-1">
                  Vincula tu WhatsApp personal para chatear con OkBot desde cualquier lugar
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3 text-left">
                {[
                  { e: '📝', t: 'Notas y listas' },
                  { e: '📅', t: 'Agenda diaria' },
                  { e: '💰', t: 'Control financiero' },
                  { e: '🤖', t: 'Agentes de IA' },
                ].map(f => (
                  <div key={f.e} className="bg-white/5 rounded-xl p-3 flex items-center gap-2">
                    <span className="text-xl">{f.e}</span>
                    <span className="text-white text-xs font-medium">{f.t}</span>
                  </div>
                ))}
              </div>
              <p className="text-white text-sm">
                Para vincular tu WhatsApp, ve al chat principal y presiona el botón
                <span className="inline-flex items-center gap-1 mx-1 px-2 py-0.5 bg-green-600/20 border border-green-500/30 rounded text-white text-xs font-medium">
                  <MessageCircle size={10} />Envíame un WhatsApp
                </span>
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── TAB: Tokens ── */}
      {activeTab === 'tokens' && (
        <div className="space-y-4">
          {/* Nuevo token alert si se acaba de crear */}
          {newToken && (
            <div className="glass-panel p-5 rounded-2xl border border-green-500/30 bg-green-500/5">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 size={18} className="text-green-400" />
                <p className="text-green-400 font-semibold">¡Token generado! Guárdalo ahora</p>
              </div>
              <p className="text-gray-400 text-xs mb-3 flex items-center gap-1.5">
                <AlertCircle size={12} className="text-amber-400" />
                Este token <strong className="text-amber-300">solo se muestra una vez</strong>. Cópialo antes de cerrar.
              </p>
              <div className="flex items-center gap-2 bg-black/40 border border-white/10 rounded-xl p-3">
                <code className={`flex-1 font-mono text-sm break-all ${showToken ? 'text-green-300' : 'text-gray-600'}`}>
                  {showToken ? newToken.token : newToken.token.replace(/./g, '•')}
                </code>
                <button onClick={() => setShowToken(v => !v)} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400">
                  {showToken ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
                <CopyButton text={newToken.token} label="Copiar" />
              </div>
              <button onClick={() => setNewToken(null)} className="mt-3 text-gray-500 hover:text-gray-300 text-xs transition-colors">
                Ya lo guardé, cerrar
              </button>
            </div>
          )}

          {/* Formulario nuevo token */}
          <div className="glass-panel p-5 rounded-2xl space-y-3">
            <h2 className="text-white font-semibold flex items-center gap-2">
              <Plus size={17} className="text-violet-400" />Nuevo token de API
            </h2>
            <p className="text-gray-500 text-sm">Dale un nombre descriptivo para recordar dónde lo usarás (ej. "Claude Desktop personal")</p>
            <div className="flex gap-2">
              <input
                value={newTokenName}
                onChange={e => setNewTokenName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && generateToken()}
                placeholder="ej. Claude Desktop Mac, Cursor trabajo..."
                className="input-glass flex-1 text-sm"
                maxLength={80}
              />
              <button onClick={generateToken} disabled={generating || !newTokenName.trim()}
                className="btn-primary px-5 flex items-center gap-2 text-sm whitespace-nowrap disabled:opacity-50">
                {generating ? <RefreshCw size={14} className="animate-spin" /> : <Key size={14} />}
                Generar
              </button>
            </div>
          </div>

          {/* Lista de tokens */}
          {loading ? (
            <div className="text-center py-10 text-gray-500">
              <RefreshCw size={20} className="animate-spin mx-auto mb-2 opacity-50" />Cargando tokens...
            </div>
          ) : tokens.length === 0 ? (
            <div className="glass-panel p-8 rounded-2xl text-center">
              <Key size={32} className="text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400 font-medium">No tienes tokens de API</p>
              <p className="text-gray-600 text-sm mt-1">Genera uno arriba para conectar tus asistentes de IA</p>
            </div>
          ) : (
            <div className="space-y-3">
              {tokens.map(t => <TokenCard key={t.token_id} token={t} onRevoke={revokeToken} />)}
            </div>
          )}
        </div>
      )}

      {/* ── TAB: Conectar ── */}
      {activeTab === 'connect' && (
        <div className="space-y-4">
          <div className="glass-panel p-4 rounded-2xl bg-amber-500/5 border border-amber-500/20">
            <p className="text-amber-300 text-sm flex items-center gap-2">
              <Shield size={15} />Para conectar un cliente, primero genera un token en la pestaña "Mis tokens"
            </p>
          </div>

          {MCP_CLIENTS.map(client => (
            <div key={client.id} className={`glass-panel rounded-2xl overflow-hidden border ${client.border}`}>
              <button
                onClick={() => setExpandedClient(expandedClient === client.id ? null : client.id)}
                className={`w-full p-5 flex items-center gap-4 bg-gradient-to-r ${client.color} hover:opacity-90 transition-opacity text-left`}>
                <span className="text-3xl">{client.icon}</span>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-white font-semibold">{client.name}</p>
                    {client.download && (
                      <a href={client.download} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                        className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${client.badge_color} flex items-center gap-1`}>
                        <ExternalLink size={9} />Descargar
                      </a>
                    )}
                  </div>
                  <p className="text-gray-400 text-sm">{client.description}</p>
                </div>
                {expandedClient === client.id ? <ChevronDown size={18} className="text-gray-400 flex-shrink-0" /> : <ChevronRight size={18} className="text-gray-400 flex-shrink-0" />}
              </button>

              {expandedClient === client.id && (
                <div className="p-5 space-y-4 border-t border-white/5">
                  {client.id === 'claude-desktop' && (
                    <ol className="text-sm text-gray-400 space-y-3">
                      <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-white/10 text-white text-xs flex items-center justify-center flex-shrink-0 font-bold">1</span>
                        <span>Descarga e instala Claude Desktop desde <a href="https://claude.ai/download" target="_blank" rel="noreferrer" className="text-violet-400 hover:underline">claude.ai/download</a></span></li>
                      <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-white/10 text-white text-xs flex items-center justify-center flex-shrink-0 font-bold">2</span>
                        <span>Genera un token de API en la pestaña "Mis tokens"</span></li>
                      <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-white/10 text-white text-xs flex items-center justify-center flex-shrink-0 font-bold">3</span>
                        <span>Abre la configuración de Claude Desktop → <strong className="text-white">Settings → Developer → MCP Servers</strong></span></li>
                      <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-white/10 text-white text-xs flex items-center justify-center flex-shrink-0 font-bold">4</span>
                        <span>Pega la siguiente configuración reemplazando <code className="text-green-300 bg-black/30 px-1 rounded">TU_TOKEN</code> con el que generaste:</span></li>
                    </ol>
                  )}

                  {client.id === 'cursor' && (
                    <ol className="text-sm text-gray-400 space-y-3">
                      <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-white/10 text-white text-xs flex items-center justify-center flex-shrink-0 font-bold">1</span>
                        <span>En Cursor, abre <strong className="text-white">Settings → MCP</strong> (o Windsurf → Settings → Cascade → MCP)</span></li>
                      <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-white/10 text-white text-xs flex items-center justify-center flex-shrink-0 font-bold">2</span>
                        <span>Crea el archivo <code className="text-green-300 bg-black/30 px-1 rounded">~/.cursor/mcp.json</code> con el contenido siguiente:</span></li>
                    </ol>
                  )}

                  {client.id === 'windsurf' && (
                    <ol className="text-sm text-gray-400 space-y-3">
                      <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-white/10 text-white text-xs flex items-center justify-center flex-shrink-0 font-bold">1</span>
                        <span>Ve a <strong className="text-white">Windsurf → Settings → Cascade → MCP Servers</strong></span></li>
                      <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-white/10 text-white text-xs flex items-center justify-center flex-shrink-0 font-bold">2</span>
                        <span>Agrega un nuevo servidor con la URL y el token que generaste</span></li>
                    </ol>
                  )}

                  {client.id === 'custom' && (
                    <p className="text-sm text-gray-400">Cualquier cliente compatible con MCP/JSON-RPC 2.0 puede conectarse. Usa el endpoint SSE para streaming o el endpoint HTTP para llamadas directas:</p>
                  )}

                  {/* Snippet de config */}
                  <div className="relative">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-500 text-xs font-medium font-mono">
                        {client.id === 'claude-desktop' ? '~/.config/claude/claude_desktop_config.json' :
                         client.id === 'cursor' ? '~/.cursor/mcp.json' :
                         client.id === 'windsurf' ? '~/.codeium/windsurf/mcp_config.json' : 'Configuración'}
                      </span>
                      <CopyButton text={getConfig(client.config_key, 'TU_TOKEN_AQUI', user.id)} label="Copiar config" />
                    </div>
                    <pre className="text-xs text-green-300 bg-black/50 p-4 rounded-xl overflow-x-auto font-mono leading-relaxed border border-white/5 whitespace-pre-wrap">
                      {getConfig(client.config_key, 'TU_TOKEN_AQUI', user.id)}
                    </pre>
                  </div>

                  {/* Endpoints útiles */}
                  <div className="space-y-2">
                    <p className="text-gray-500 text-xs font-medium">Endpoints del servidor:</p>
                    {[
                      { label: 'HTTP (JSON-RPC)', url: `${window.location.origin}/api/mcp`, method: 'POST' },
                      { label: 'SSE (streaming)', url: `${window.location.origin}/api/mcp/sse`, method: 'GET' },
                      { label: 'Lista de herramientas', url: `${window.location.origin}/api/mcp/tools`, method: 'GET' },
                    ].map(ep => (
                      <div key={ep.label} className="flex items-center gap-2 p-2.5 bg-black/30 rounded-xl">
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${ep.method === 'POST' ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'}`}>{ep.method}</span>
                        <code className="text-gray-300 text-xs flex-1 font-mono truncate">{ep.url}</code>
                        <CopyButton text={ep.url} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── TAB: Herramientas ── */}
      {activeTab === 'tools' && (
        <div className="space-y-3">
          <div className="glass-panel p-4 rounded-2xl">
            <p className="text-gray-400 text-sm">
              Estas son las <strong className="text-white">{tools.length} herramientas</strong> que tu asistente de IA puede usar en tiempo real a través de OkBot MCP.
            </p>
          </div>

          {Object.entries(toolsByCategory).map(([cat, catTools]) => {
            const meta = TOOL_CATEGORIES[cat] || { label: cat, emoji: '🔧', color: 'text-gray-400' };
            const isOpen = expandedCategory === cat;
            return (
              <div key={cat} className="glass-panel rounded-2xl overflow-hidden">
                <button onClick={() => setExpandedCategory(isOpen ? null : cat)}
                  className="w-full p-4 flex items-center gap-3 hover:bg-white/5 transition-colors text-left">
                  <span className="text-xl">{meta.emoji}</span>
                  <div className="flex-1">
                    <p className="text-white font-medium text-sm">{meta.label}</p>
                    <p className="text-gray-500 text-xs">{catTools.length} herramienta{catTools.length !== 1 ? 's' : ''}</p>
                  </div>
                  {isOpen ? <ChevronDown size={16} className="text-gray-500" /> : <ChevronRight size={16} className="text-gray-500" />}
                </button>

                {isOpen && (
                  <div className="border-t border-white/5 p-3 grid grid-cols-1 gap-2">
                    {catTools.map(tool => (
                      <div key={tool.name} className="p-3 bg-white/5 rounded-xl">
                        <div className="flex items-start justify-between gap-2">
                          <code className={`text-xs font-mono font-semibold ${meta.color}`}>{tool.name}</code>
                          <span className="text-[10px] bg-white/10 text-gray-400 px-2 py-0.5 rounded-full flex-shrink-0">{cat}</span>
                        </div>
                        <p className="text-gray-500 text-xs mt-1 leading-relaxed">{tool.description}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── TAB: Guía rápida ── */}
      {activeTab === 'guide' && (
        <div className="space-y-4">
          <div className="glass-panel p-6 rounded-2xl space-y-5">
            <h2 className="text-white font-bold text-lg flex items-center gap-2">
              <BookOpen size={20} className="text-violet-400" />¿Cómo funciona?
            </h2>

            <div className="space-y-4">
              {[
                {
                  title: 'Protocolo MCP',
                  icon: '🔌',
                  text: 'OkBot implementa el Model Context Protocol (MCP) de Anthropic — el estándar abierto para conectar asistentes de IA con herramientas y datos externos de forma segura.',
                },
                {
                  title: 'Autenticación segura por token',
                  icon: '🔑',
                  text: 'Cada token de API se genera una sola vez, se almacena como hash SHA-256 en la base de datos y nunca viaja en texto plano después de su creación. Puedes revocar tokens en cualquier momento.',
                },
                {
                  title: '¿Qué puede hacer tu asistente?',
                  icon: '🤖',
                  text: 'Con el token configurado, tu Claude Desktop (u otro cliente) puede: crear notas, agregar recordatorios, ver tu calendario, registrar gastos de finanzas, consultar tus feeds, buscar en la web, y mucho más — todo desde la interfaz del asistente.',
                },
                {
                  title: 'Privacidad',
                  icon: '🛡️',
                  text: 'El servidor MCP solo comparte los datos del usuario al que pertenece el token. Ningún otro usuario puede ver tus datos. Puedes revocar el acceso en cualquier momento desde esta pantalla.',
                },
                {
                  title: 'Ejemplo real',
                  icon: '💬',
                  text: 'Le preguntas a Claude: "¿Cuánto gasté en comida este mes?" — Claude llama a la herramienta get_finance_summary en OkBot, recibe tus datos reales y te responde con información concreta de tu cuenta.',
                },
              ].map(item => (
                <div key={item.title} className="flex gap-4 p-4 bg-white/5 rounded-2xl">
                  <span className="text-2xl flex-shrink-0 mt-0.5">{item.icon}</span>
                  <div>
                    <p className="text-white font-medium text-sm mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="glass-panel p-5 rounded-2xl border border-violet-500/20 bg-violet-500/5">
            <p className="text-gray-400 text-sm leading-relaxed">
              <strong className="text-violet-300">¿Necesitas ayuda?</strong> Ve a la sección de <strong className="text-white">Soporte</strong> desde el menú lateral o contacta al equipo OkBot.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegrationsModule;
